import React, { useState, useRef, useEffect } from 'react';
import type { AgentApiCompaniesItem } from '@/types/serializers';
import clsx from 'clsx';
import type SharedCustomField from '@/types/serializers/SharedCustomField';
import CandidatesUserJobProcessScore from '../../candidates/components/CandidatesUserJobProcessScore';
import { MdArrowDropDown, MdArrowDropUp } from 'react-icons/md';
import { Link } from '@tanstack/react-router';
import { Tag, type TagColor } from '@/components/Tag';
import CompanyTagSelectPopover from './CompanyTagSelectPopover';
import Checkbox from '@/components/Checkbox';
import UsersMenu from '@/features/agent/components/UsersMenu';
import type { ActiveUser } from '../../types/ActiveUser';

type Props = {
  companies: AgentApiCompaniesItem[];
  visibleFields: string[];
  customFields: SharedCustomField[];
  selectedIds?: Set<number>;
  onCheckedChange?: (id: number, checked: boolean) => void;
  activeUsers?: ActiveUser[];
  currentUserId?: number;
  onUpdateAssignees?: (companyId: number, userIds: number[]) => void;
};

const truncateText = (text: string | null | undefined): string => {
  if (!text) return '';
  return text.length > 20 ? `${text.substring(0, 20)}...` : text;
};

type OpenDetailType = {
  companyId: number | null;
  fieldName: string | null;
};

type ExpandableTextProps = {
  text: string;
  companyId: number;
  fieldName: string;
  isOpen: boolean;
  onToggle: (companyId: number, fieldName: string) => void;
};

const ExpandableText = ({ text, companyId, fieldName, isOpen, onToggle }: ExpandableTextProps) => {
  const needsExpand = text.length > 20;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onToggle(companyId, fieldName);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, companyId, fieldName, onToggle]);

  return (
    <div className="relative flex gap-xs" ref={containerRef}>
      <span className="text-body-xs">{truncateText(text)}</span>
      {needsExpand && (
        <button
          type="button"
          className={clsx(
            'flex size-icon-xs items-center justify-center rounded border border-solid border-border',
            isOpen && 'bg-gray_darken'
          )}
          onClick={() => onToggle(companyId, fieldName)}
        >
          {isOpen ? <MdArrowDropUp size={12} /> : <MdArrowDropDown size={12} />}
        </button>
      )}
      {isOpen && needsExpand && (
        <div className="absolute top-full -left-xl z-10 mt-1 rounded border border-solid border-border bg-white shadow-md">
          <div className="hidden-scrollbar max-h-[196px] w-[320px] overflow-x-hidden overflow-y-scroll px-sm py-xs">
            <p className="text-xs leading-[170%] text-wrap break-words">{text}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default function CompaniesList({
  companies,
  visibleFields,
  customFields,
  selectedIds,
  onCheckedChange,
  activeUsers = [],
  currentUserId,
  onUpdateAssignees,
}: Props) {
  const [openDetail, setOpenDetail] = useState<OpenDetailType>({
    companyId: null,
    fieldName: null,
  });

  const toggleDetail = (companyId: number, fieldName: string) => {
    if (openDetail.companyId === companyId && openDetail.fieldName === fieldName) {
      setOpenDetail({ companyId: null, fieldName: null });
    } else {
      setOpenDetail({ companyId, fieldName });
    }
  };

  const isDetailOpen = (companyId: number, fieldName: string) => {
    return openDetail.companyId === companyId && openDetail.fieldName === fieldName;
  };
  return (
    <div className="flex flex-col gap-4">
      {companies.map((c, key) => {
        return (
          <div className="rounded-sm bg-white p-xl shadow-md" key={key}>
            <div
              className={clsx(
                'flex border-b border-solid border-border pb-lg',
                visibleFields.length === 0 && 'border-none! pb-0!'
              )}
            >
              <div className="flex w-full gap-md">
                {selectedIds !== undefined && onCheckedChange && (
                  <div className="flex items-start pt-1">
                    <Checkbox
                      checked={selectedIds.has(c.id)}
                      onChange={(checked) => onCheckedChange(c.id, checked)}
                    />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-md">
                    <Link
                      to="/agent/companies/$companiesId"
                      params={{ companiesId: c.id.toString() }}
                      className="flex items-center gap-xxs text-heading-sm font-bold text-main"
                    >
                      {truncateText(c.name)}
                    </Link>
                    {onUpdateAssignees && (
                      <UsersMenu
                        activeUsers={activeUsers}
                        selectedUserIds={c.organization_users?.map((u) => u.id) ?? []}
                        onSelect={(userIds) => onUpdateAssignees(c.id, userIds)}
                        currentUserId={currentUserId}
                      />
                    )}
                  </div>
                  <div className="mt-sm flex flex-wrap items-center gap-1">
                    {c.tags.map((tag) => (
                      <Tag key={tag.id} color={tag.color_hex as TagColor} size="sm">
                        {tag.name}
                      </Tag>
                    ))}
                    <CompanyTagSelectPopover
                      id={c.id}
                      tags={c.tags.map((tag) => ({
                        id: tag.id,
                        name: tag.name,
                        colorHex: tag.color_hex,
                      }))}
                    />
                  </div>
                  <div className="mt-md flex items-center gap-lg">
                    <div className="flex items-center gap-xs">
                      <p className="text-xs font-bold">所在地</p>
                      {c.company_information?.address ? (
                        <p className="text-xs">{truncateText(c.company_information?.address)}</p>
                      ) : (
                        <p className="text-xs text-disabled">未設定</p>
                      )}
                    </div>
                    <div className="flex items-center gap-xs">
                      <p className="text-xs font-bold">代表者名</p>
                      {c.company_information?.president_name ? (
                        <p className="text-xs">
                          {truncateText(c.company_information?.president_name)}
                        </p>
                      ) : (
                        <p className="text-xs text-disabled">未設定</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="ml-auto flex gap-md">
                  <CandidatesUserJobProcessScore
                    text="提案数"
                    score={c.suggested_job_posts_count || 0}
                  />
                  <CandidatesUserJobProcessScore text="マッチング数" score={c.matched_count || 0} />
                  <CandidatesUserJobProcessScore
                    text="求人数"
                    score={c.provided_job_posts_count || 0}
                  />
                </div>
              </div>
            </div>
            {visibleFields.length !== 0 && (
              <div className="mt-md flex flex-wrap gap-x-md gap-y-sm">
                {visibleFields.includes('listed_phase') && (
                  <>
                    <div className="flex items-center gap-xs text-main">
                      <div className="text-heading-3xs font-bold">上場情報</div>
                      {c.company_information?.listed_phase ? (
                        <ExpandableText
                          text={c.company_information?.listed_phase}
                          companyId={c.id}
                          fieldName="listed_phase"
                          isOpen={isDetailOpen(c.id, 'listed_phase')}
                          onToggle={toggleDetail}
                        />
                      ) : (
                        <p className="text-xs text-disabled">未設定</p>
                      )}
                    </div>
                    <span className="border-y-0 border-r border-l-0 border-solid border-border"></span>
                  </>
                )}
                {visibleFields.includes('official_url') && (
                  <>
                    <div className="flex items-center gap-xs text-main">
                      <div className="text-heading-3xs font-bold">URL</div>
                      {c.company_information?.official_url ? (
                        <ExpandableText
                          text={c.company_information?.official_url}
                          companyId={c.id}
                          fieldName="official_url"
                          isOpen={isDetailOpen(c.id, 'official_url')}
                          onToggle={toggleDetail}
                        />
                      ) : (
                        <p className="text-xs text-disabled">未設定</p>
                      )}
                    </div>
                    <span className="border-y-0 border-r border-l-0 border-solid border-border"></span>
                  </>
                )}
                {visibleFields.includes('industry') && (
                  <>
                    <div className="flex items-center gap-xs text-main">
                      <div className="text-heading-3xs font-bold">業種</div>
                      {c.company_information?.industry ? (
                        <ExpandableText
                          text={c.company_information?.industry}
                          companyId={c.id}
                          fieldName="industry"
                          isOpen={isDetailOpen(c.id, 'industry')}
                          onToggle={toggleDetail}
                        />
                      ) : (
                        <p className="text-xs text-disabled">未設定</p>
                      )}
                    </div>
                    <span className="border-y-0 border-r border-l-0 border-solid border-border"></span>
                  </>
                )}
                {visibleFields.includes('description') && (
                  <>
                    <div className="flex items-center gap-xs text-main">
                      <div className="text-heading-3xs font-bold">事業内容</div>
                      {c.company_information?.description ? (
                        <ExpandableText
                          text={c.company_information.description}
                          companyId={c.id}
                          fieldName="description"
                          isOpen={isDetailOpen(c.id, 'description')}
                          onToggle={toggleDetail}
                        />
                      ) : (
                        <p className="text-xs text-disabled">未設定</p>
                      )}
                    </div>
                    <span className="border-y-0 border-r border-l-0 border-solid border-border"></span>
                  </>
                )}
                {visibleFields.includes('number_of_employees') && (
                  <>
                    <div className="flex items-center gap-xs text-main">
                      <div className="text-heading-3xs font-bold">従業員数</div>
                      {c.company_information?.number_of_employees ? (
                        <ExpandableText
                          text={c.company_information?.number_of_employees + '人'}
                          companyId={c.id}
                          fieldName="number_of_employees"
                          isOpen={isDetailOpen(c.id, 'number_of_employees')}
                          onToggle={toggleDetail}
                        />
                      ) : (
                        <p className="text-xs text-disabled">未設定</p>
                      )}
                    </div>
                    <span className="border-y-0 border-r border-l-0 border-solid border-border"></span>
                  </>
                )}
                {visibleFields.includes('established_date') && (
                  <>
                    <div className="flex items-center gap-xs text-main">
                      <div className="text-heading-3xs font-bold">設立年</div>
                      {c.company_information?.established_date ? (
                        <ExpandableText
                          text={c.company_information?.established_date}
                          companyId={c.id}
                          fieldName="established_date"
                          isOpen={isDetailOpen(c.id, 'established_date')}
                          onToggle={toggleDetail}
                        />
                      ) : (
                        <p className="text-xs text-disabled">未設定</p>
                      )}
                    </div>
                    {customFields.some((field) =>
                      visibleFields.includes(`custom_field_${field.id}`)
                    ) && (
                      <span className="border-y-0 border-r border-l-0 border-solid border-border"></span>
                    )}
                  </>
                )}

                {customFields.map((field) => {
                  const fieldId = `custom_field_${field.id}`;
                  if (!visibleFields.includes(fieldId)) return null;

                  const customData = c.custom_data.find(
                    (data) => data.custom_field.id === field.id
                  );

                  return (
                    <React.Fragment key={fieldId}>
                      <div className="flex items-center gap-xs text-main">
                        <div className="text-heading-3xs font-bold">{field.name}</div>
                        {customData?.content ? (
                          <ExpandableText
                            text={
                              customData.content + (field.unit_name ? ` ${field.unit_name}` : '')
                            }
                            companyId={c.id}
                            fieldName={`custom_field_${field.id}`}
                            isOpen={isDetailOpen(c.id, `custom_field_${field.id}`)}
                            onToggle={toggleDetail}
                          />
                        ) : (
                          <p className="text-xs text-disabled">未設定</p>
                        )}
                      </div>
                      {field.id !== customFields[customFields.length - 1].id && (
                        <span className="border-y-0 border-r border-l-0 border-solid border-border"></span>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
